import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Stack,
  Title,
  Text,
  DescriptionList,
  Accordion,
  Icon,
  Tooltip,
  IconButton,
  DefaultPageSkeleton,
  Select,
  Menu,
  Alert,
  Checkbox
} from '@efast_public/fjd-component-library';
import { useQuery } from '@tanstack/react-query';
import { EditKontaktinformationenModal } from '../modals/EditKontaktinformationenModal';
import { EditSystempersonalisierungModal } from '../modals/EditSystempersonalisierungModal';
import {
  EditMarketplaceAccessKeysModal,
  parseAccessKeys
} from '../modals/EditMarketplaceAccessKeysModal';
import { AddMemberModal } from '../modals/AddMemberModal';
import { EditMembersModal, Member } from '../modals/EditMembersModal';
import { ChangeOwnerModal } from '../modals/ChangeOwnerModal';
import { AddZustaendigeStelleModal } from '../modals/AddZustaendigeStelleModal';
import { RenameZustaendigeStelleModal } from '../modals/RenameZustaendigeStelleModal';
import { DeleteZustaendigeStelleModal } from '../modals/DeleteZustaendigeStelleModal';
import { usePartnerFetcher } from '../api/hooks/usePartnerFetcher';
import { GovosApiRoutes, govosClient } from '../api/govos-api';
import { ApiMember } from '../api/govos.types';
import { urlToGovosStartApp } from '../utils/urlUtil';
import { VVExpertModeRedirectModal } from '../modals/VVExpertModeRedirectModal';
import { LocalStorageKeys } from '../utils/odUtil';

export function ZustaendigeStellePage() {
  const { t } = useTranslation();

  const [isContactInfoModalVisible, setIsContactInfoModalVisible] =
    useState<boolean>(false);
  const [
    isSystemPersonalizationModalVisible,
    setIsSystemPersonalizationModalVisible
  ] = useState<boolean>(false);
  const [isAccessKeysModalVisible, setIsAccessKeysModalVisible] =
    useState<boolean>(false);
  const [isAddMemberModalVisible, setIsAddMemberModalVisible] =
    useState<boolean>(false);
  const [isEditMembersModalVisible, setIsEditMembersModalVisible] =
    useState<boolean>(false);
  const [isChangeOwnerModalVisible, setIsChangeOwnerModalVisible] =
    useState<boolean>(false);
  const [
    isAddZustaendigeStelleModalVisible,
    setIsAddZustaendigeStelleModalVisible
  ] = useState<boolean>(false);
  const [
    isRenameZustaendigeStelleModalVisible,
    setIsRenameZustaendigeStelleModalVisible
  ] = useState<boolean>(false);
  const [
    isDeleteZustaendigeStelleModalVisible,
    setIsDeleteZustaendigeStelleModalVisible
  ] = useState<boolean>(false);
  const [memberMenuAnchor, setMemberMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(
    null
  );
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [redirectUrlToExportMode, setRedirectUrlToExportMode] = useState<
    string | null
  >(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [isFilterErrors, setIsFilterErrors] = useState<boolean>(false);

  const { data: partners, isLoading, startApps } = usePartnerFetcher();

  const selectedPartner =
    partners?.find((p) => String(p.appiid) === selectedPartnerId) ??
    partners.at(0);

  // Validate all partners to check for errors
  const partnersWithErrors = useMemo(() => {
    if (!partners) return [];
    return partners.filter((partner) => {
      const errors: string[] = [];
      if (!partner.dispname || partner.dispname.trim() === '') {
        errors.push('name');
      }
      if (!partner.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(partner.email)) {
        errors.push('email');
      }
      if (!partner.orgname1 || partner.orgname1.trim() === '') {
        errors.push('organizationName');
      }
      return errors.length > 0;
    });
  }, [partners]);

  const hasErrors = partnersWithErrors.length > 0;

  // Collect human-readable errors for the currently selected partner.
  const selectedPartnerErrors = useMemo<string[]>(() => {
    if (!selectedPartner) return [];
    const errors: string[] = [];
    if (!selectedPartner.dispname || selectedPartner.dispname.trim() === '') {
      errors.push(t('ZustaendigeStelle.errors.missingName'));
    }
    if (
      !selectedPartner.email ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(selectedPartner.email)
    ) {
      errors.push(t('ZustaendigeStelle.errors.missingEmail'));
    }
    if (!selectedPartner.orgname1 || selectedPartner.orgname1.trim() === '') {
      errors.push(t('ZustaendigeStelle.errors.missingOrganization'));
    }
    return errors;
  }, [selectedPartner, t]);

  // Filter partners based on error filter checkbox
  const filteredPartners = useMemo(() => {
    if (!partners) return [];
    if (isFilterErrors) return partnersWithErrors;
    return partners;
  }, [partners, isFilterErrors, partnersWithErrors]);

  // Fetch members for the selected partner
  const { data: members = [] } = useQuery<Member[]>({
    queryKey: [GovosApiRoutes.members(selectedPartner?.appiid || 0)],
    queryFn: async () => {
      if (!selectedPartner?.appiid) return [];
      const response = await govosClient.get(
        GovosApiRoutes.members(selectedPartner.appiid)
      );
      const data = response.data;

      // Handle response structure: { result: "success", members: [...] }
      if (data?.result === 'success' && Array.isArray(data.members)) {
        // Map API response to Member interface
        return data.members
          .map((member: ApiMember) => {
            // Map role values: 6 = owner, 5 = moderator, 4 = member
            let role: 'owner' | 'member' | 'moderator' = 'member';
            if (member.role === 6) {
              role = 'owner';
            } else if (member.role === 5) {
              role = 'moderator';
            } else if (member.role === 4) {
              role = 'member';
            }

            return {
              id: String(member.userid),
              name: member.dispname,
              email: member.email,
              role
            };
          })
          .sort((a: Member, b: Member) => {
            // Sort: owner first, then moderator, then member
            const roleOrder = { owner: 0, moderator: 1, member: 2 } as const;
            return (
              roleOrder[a.role as keyof typeof roleOrder] -
              roleOrder[b.role as keyof typeof roleOrder]
            );
          });
      }

      return [];
    },
    enabled: !!selectedPartner?.appiid
  });

  useEffect(() => {
    if (!selectedPartnerId) {
      const fallback = filteredPartners[0] ?? partners?.[0];
      if (fallback) {
        setSelectedPartnerId(String(fallback.appiid));
        if (!isInitialized) {
          setIsInitialized(true);
        }
      }
    }
  }, [partners, filteredPartners, selectedPartnerId, isInitialized]);

  const handleRedirectLink = (url: string) => {
    if (
      localStorage.getItem(
        LocalStorageKeys.shouldRedirectInstantlyToExpertMode
      ) === 'true'
    ) {
      window.open(url, '_blank');
    } else {
      setRedirectUrlToExportMode(url);
    }
  };

  if (isLoading) {
    return <DefaultPageSkeleton />;
  }

  return (
    <Stack spacing="l" orientation="vertical">
      {/* Header */}
      <Stack spacing="s" orientation="vertical">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}
        >
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px'
              }}
            >
              <Title level="2">{t('ZustaendigeStelle.title')}</Title>
              <Tooltip tooltip={t('ZustaendigeStelle.titleTooltip')}>
                <IconButton
                  variant="primary-text"
                  label=""
                  icon="information-filled"
                  size="sm"
                />
              </Tooltip>
            </div>
            <Text size="s">
              {t('ZustaendigeStelle.subtitle', {
                amount: startApps?.userInfo.startApps.length ?? 0
              })}
            </Text>
          </div>
          <Stack spacing="xs" direction="row" alignItems="center">
            <Button
              variant="primary"
              beforeIcon="add"
              onClick={() => setIsAddZustaendigeStelleModalVisible(true)}
            >
              {t('ZustaendigeStelle.addButton')}
            </Button>
          </Stack>
        </div>

        {/* Error Alert (custom: Alert + Checkbox stacked inside one blue box) */}
        {hasErrors && (
          <div
            style={{
              backgroundColor: 'var(--fjd2-semantic-info-background)',
              border: 'thin solid var(--fjd2-semantic-info-border)',
              borderRadius: 'var(--fjd2-border-radius-s)',
              padding: 'var(--fjd2-spacing-m)'
            }}
          >
            <Stack spacing="s">
              <Stack spacing="s" direction="row" alignItems="center">
                <Icon name="information-filled" variant="info" size="md" />
                <Text>
                  {t('ZustaendigeStelle.validationErrorsAlert', {
                    count: partnersWithErrors.length
                  })}
                </Text>
              </Stack>
              <div style={{ paddingLeft: '32px' }}>
                <Checkbox
                  checked={isFilterErrors}
                  onChange={(e) => setIsFilterErrors(e.currentTarget.checked)}
                  label={t('ZustaendigeStelle.filterErrorsCheckbox')}
                />
              </div>
            </Stack>
          </div>
        )}

        {/* Partner Selection */}
        <Stack style={{ width: '100%' }}>
          {filteredPartners &&
            (() => {
              const partnersWithFlags =
                filteredPartners?.map((partner) => {
                  const hasError =
                    !partner.dispname ||
                    partner.dispname.trim() === '' ||
                    !partner.email ||
                    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(partner.email) ||
                    !partner.orgname1 ||
                    partner.orgname1.trim() === '';
                  return { partner, hasError };
                }) ?? [];
              const partnerOptions = partnersWithFlags.map(
                ({ partner, hasError }) => ({
                  value: String(partner.appiid),
                  subline: partner.startApp.name,
                  label:
                    partner.dispname ||
                    `${t('ZustaendigeStelle.unnamed')} (${partner.startApp.name})`,
                  prefix: (
                    <Icon
                      name={hasError ? 'warning-filled' : 'checkmark-filled'}
                      style={{
                        color: hasError ? '#f97316' : '#16a34a'
                      }}
                    />
                  )
                })
              );
              const effectiveSelectedId = selectedPartner
                ? String(selectedPartner.appiid)
                : null;
              const selectedOption = partnerOptions.find(
                (o) => o.value === effectiveSelectedId
              );
              const hasSelectedError = partnersWithFlags.find(
                ({ partner }) => String(partner.appiid) === effectiveSelectedId
              )?.hasError;
              const selectedIcon =
                hasSelectedError === undefined
                  ? undefined
                  : hasSelectedError
                    ? 'warning-filled'
                    : 'checkmark-filled';
              const statusClass =
                hasSelectedError === undefined
                  ? undefined
                  : hasSelectedError
                    ? 'zs-select-status--error'
                    : 'zs-select-status--ok';
              return (
                <div className={statusClass} style={{ width: '100%' }}>
                  <Select
                    selectedOptions={selectedOption ? [selectedOption] : []}
                    onChange={(options) => {
                      const opt = options.at(0);
                      setSelectedPartnerId(opt?.value ?? null);
                    }}
                    options={partnerOptions}
                    placeholder={
                      filteredPartners?.length === 0
                        ? t('select.noOptionsAvailable')
                        : t('ZustaendigeStelle.selectPartnerPlaceholder')
                    }
                    disabled={filteredPartners?.length === 0}
                    style={{ width: '100%' }}
                    {...({ iconBefore: selectedIcon } as {
                      iconBefore?: string;
                    })}
                  />
                </div>
              );
            })()}
        </Stack>
      </Stack>

      {selectedPartner && (
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              marginBottom: '16px'
            }}
          >
            <Stack spacing="xs" direction="row">
              <Button
                variant="secondary-outline"
                beforeIcon="arrow-up-right"
                onClick={() =>
                  handleRedirectLink(
                    urlToGovosStartApp(
                      selectedPartnerId ?? partners.at(0)?.appiid ?? 0
                    )
                  )
                }
              >
                {t('ZustaendigeStelle.redirectButton')}
              </Button>
              <IconButton
                variant="primary-text"
                label=""
                icon="overflow-menu-vertical"
                size="lg"
                onClick={(e) => setMenuAnchor(e.currentTarget)}
              />
              <Menu
                anchorElement={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
              >
                <Menu.MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    setIsRenameZustaendigeStelleModalVisible(true);
                  }}
                >
                  {t('ZustaendigeStelle.renameButton')}
                </Menu.MenuItem>
                <Menu.MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    setIsDeleteZustaendigeStelleModalVisible(true);
                  }}
                >
                  {t('ZustaendigeStelle.deleteButton')}
                </Menu.MenuItem>
              </Menu>
            </Stack>
          </div>

          {/* Per-partner error alert */}
          {selectedPartnerErrors.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <Alert
                variant="warning"
                message={
                  <Stack spacing="xs" orientation="vertical">
                    {selectedPartnerErrors.map((error) => (
                      <Text key={error}>• {error}</Text>
                    ))}
                  </Stack>
                }
              />
            </div>
          )}

          {/* Contact Information Accordion */}
          <Accordion.Group>
            <div
              style={
                selectedPartnerErrors.length > 0
                  ? {
                      border: '1px solid #f97316',
                      borderRadius: 'var(--fjd2-border-radius-s, 4px)'
                    }
                  : undefined
              }
            >
              <Accordion
                label={t('ZustaendigeStelle.sections.contactInfo')}
                after={
                  <Button
                    variant="primary-text"
                    size="sm"
                    beforeIcon="edit"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsContactInfoModalVisible(true);
                    }}
                  >
                    {t('ZustaendigeStelle.editButton')}
                  </Button>
                }
                expanded
              >
                <DescriptionList hasDivider>
                  <DescriptionList.Item
                    key="designation"
                    title={
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <span>{t('ZustaendigeStelle.fields.designation')}</span>
                        {(!selectedPartner.dispname ||
                          selectedPartner.dispname.trim() === '') && (
                          <Icon
                            name="warning-filled"
                            size="sm"
                            style={{ color: '#f97316' }}
                          />
                        )}
                      </div>
                    }
                    description={selectedPartner.dispname || '-'}
                  />
                  <DescriptionList.Item
                    key="emailAddress"
                    title={
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <span>
                          {t('ZustaendigeStelle.fields.emailAddress')}
                        </span>
                        {(!selectedPartner.email ||
                          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                            selectedPartner.email
                          )) && (
                          <Icon
                            name="warning-filled"
                            size="sm"
                            style={{ color: '#f97316' }}
                          />
                        )}
                        <Tooltip
                          tooltip={t(
                            'ZustaendigeStelle.fields.emailAddressTooltip'
                          )}
                        >
                          <IconButton
                            variant="primary-text"
                            label=""
                            icon="information-filled"
                            size="sm"
                          />
                        </Tooltip>
                      </div>
                    }
                    description={selectedPartner.email || '-'}
                  />
                  <DescriptionList.Item
                    key="emailAddressHide"
                    title={
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <span>
                          {t('ZustaendigeStelle.fields.emailAddressHide')}
                        </span>
                        <Tooltip
                          tooltip={t(
                            'ZustaendigeStelle.fields.emailAddressHideTooltip'
                          )}
                        >
                          <IconButton
                            variant="primary-text"
                            label=""
                            icon="information-filled"
                            size="sm"
                          />
                        </Tooltip>
                      </div>
                    }
                    description={t(
                      `ZustaendigeStelle.values.hideEmailAddress.${selectedPartner.emailHide}`
                    )}
                  />
                  <DescriptionList.Item
                    key="organizationName"
                    title={
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <span>
                          {t('ZustaendigeStelle.fields.organizationName')}
                        </span>
                        {(!selectedPartner.orgname1 ||
                          selectedPartner.orgname1.trim() === '') && (
                          <Icon
                            name="warning-filled"
                            size="sm"
                            style={{ color: '#f97316' }}
                          />
                        )}
                      </div>
                    }
                    description={selectedPartner.orgname1 || '-'}
                  />
                  <DescriptionList.Item
                    key="department"
                    title={t('ZustaendigeStelle.fields.department')}
                    description={selectedPartner.orgname2 || '-'}
                  />
                  <DescriptionList.Item
                    key="ars"
                    title={t('ZustaendigeStelle.fields.ars')}
                    description={selectedPartner.ars || '-'}
                  />
                  <DescriptionList.Item
                    key="street"
                    title={t('ZustaendigeStelle.fields.street')}
                    description={
                      selectedPartner.street && selectedPartner.hsNr
                        ? `${selectedPartner.street} ${selectedPartner.hsNr}`
                        : selectedPartner.street || '-'
                    }
                  />
                  <DescriptionList.Item
                    key="postalCode"
                    title={t('ZustaendigeStelle.fields.postalCode')}
                    description={selectedPartner.plz || '-'}
                  />
                  <DescriptionList.Item
                    key="city"
                    title={t('ZustaendigeStelle.fields.city')}
                    description={selectedPartner.ort || '-'}
                  />
                  <DescriptionList.Item
                    key="phone"
                    title={t('ZustaendigeStelle.fields.phone')}
                    description={selectedPartner.phone || '-'}
                  />
                  <DescriptionList.Item
                    key="contactPageLink"
                    title={
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <span>
                          {t('ZustaendigeStelle.fields.contactPageLink')}
                        </span>
                        <Tooltip
                          tooltip={t(
                            'ZustaendigeStelle.fields.contactPageLinkTooltip'
                          )}
                        >
                          <IconButton
                            variant="primary-text"
                            label=""
                            icon="information-filled"
                            size="sm"
                          />
                        </Tooltip>
                      </div>
                    }
                    description={
                      <div style={{ display: 'flex', gap: '16px' }}>
                        <Button
                          variant="primary-text"
                          disabled={!selectedPartner.contacturl}
                          onClick={() =>
                            window.open(selectedPartner.contacturl, '_blank')
                          }
                        >
                          <Icon
                            name="link"
                            size="sm"
                            style={{ marginRight: '4px' }}
                          />
                          {t('ZustaendigeStelle.actions.callContactPage')}
                        </Button>
                        <Button
                          variant="primary-text"
                          disabled={!selectedPartner.contacturl}
                          onClick={() =>
                            navigator.clipboard.writeText(
                              selectedPartner.contacturl ?? ''
                            )
                          }
                        >
                          <Icon
                            name="copy"
                            size="sm"
                            style={{ marginRight: '4px' }}
                          />
                          {t('ZustaendigeStelle.actions.copyLink')}
                        </Button>
                      </div>
                    }
                  />
                </DescriptionList>
              </Accordion>
            </div>

            {/* System Personalization Accordion */}
            <Accordion
              label={t('ZustaendigeStelle.sections.systemPersonalization')}
              after={
                <Button
                  variant="primary-text"
                  size="sm"
                  beforeIcon="edit"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsSystemPersonalizationModalVisible(true);
                  }}
                >
                  {t('ZustaendigeStelle.editButton')}
                </Button>
              }
            >
              <DescriptionList hasDivider>
                <DescriptionList.Item
                  key="maxAttachmentSize"
                  title={
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <span>
                        {t('ZustaendigeStelle.fields.maxAttachmentSize')}
                      </span>
                      <Tooltip
                        tooltip={t(
                          'ZustaendigeStelle.fields.maxAttachmentSizeTooltip'
                        )}
                      >
                        <IconButton
                          variant="primary-text"
                          label=""
                          icon="information-filled"
                          size="sm"
                        />
                      </Tooltip>
                    </div>
                  }
                  description={`${selectedPartner.maxDocSizeInMegs} (Megabyte)`}
                />
                <DescriptionList.Item
                  key="favicon"
                  title={
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <span>{t('ZustaendigeStelle.fields.favicon')}</span>
                      <Tooltip
                        tooltip={t('ZustaendigeStelle.fields.faviconTooltip')}
                      >
                        <IconButton
                          variant="primary-text"
                          label=""
                          icon="information-filled"
                          size="sm"
                        />
                      </Tooltip>
                    </div>
                  }
                  description={
                    selectedPartner.favicon ? (
                      <img
                        src={selectedPartner.favicon}
                        alt="Favicon"
                        style={{ width: '16px', height: '16px' }}
                      />
                    ) : (
                      '-'
                    )
                  }
                />
                <DescriptionList.Item
                  key="headerLogo"
                  title={
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <span>{t('ZustaendigeStelle.fields.headerLogo')}</span>
                      <Tooltip
                        tooltip={t(
                          'ZustaendigeStelle.fields.headerLogoTooltip'
                        )}
                      >
                        <IconButton
                          variant="primary-text"
                          label=""
                          icon="information-filled"
                          size="sm"
                        />
                      </Tooltip>
                    </div>
                  }
                  description={
                    selectedPartner.headerLogo ? (
                      <img
                        src={selectedPartner.headerLogo}
                        alt="Header Logo"
                        style={{ width: '128px', height: '64px' }}
                      />
                    ) : (
                      '-'
                    )
                  }
                />
                <DescriptionList.Item
                  key="alternativeHeaderLogo"
                  title={
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <span>
                        {t('ZustaendigeStelle.fields.alternativeHeaderLogo')}
                      </span>
                      <Tooltip
                        tooltip={t(
                          'ZustaendigeStelle.fields.alternativeHeaderLogoTooltip'
                        )}
                      >
                        <IconButton
                          variant="primary-text"
                          label=""
                          icon="information-filled"
                          size="sm"
                        />
                      </Tooltip>
                    </div>
                  }
                  description={selectedPartner.bclHeaderAltText || '-'}
                />
              </DescriptionList>
            </Accordion>

            {/* Marketplace Access Keys Accordion */}
            <Accordion
              label={t('ZustaendigeStelle.sections.accessKeys')}
              after={
                <Button
                  variant="primary-text"
                  size="sm"
                  beforeIcon="edit"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsAccessKeysModalVisible(true);
                  }}
                >
                  {t('ZustaendigeStelle.editButton')}
                </Button>
              }
            >
              {(() => {
                const accessKeys = parseAccessKeys(
                  selectedPartner.marketplaceAccessKeys
                );
                if (accessKeys.length === 0) {
                  return (
                    <Text size="s">
                      {t('ZustaendigeStelle.accessKeys.empty')}
                    </Text>
                  );
                }
                return (
                  <DescriptionList hasDivider>
                    {accessKeys.map((key, index) => (
                      <DescriptionList.Item
                        key={`${index}-${key}`}
                        title={t('ZustaendigeStelle.accessKeys.keyLabel', {
                          number: index + 1
                        })}
                        description={key}
                      />
                    ))}
                  </DescriptionList>
                );
              })()}
            </Accordion>

            {/* Member Management Accordion */}
            <Accordion
              label={t('ZustaendigeStelle.sections.memberManagement')}
              after={
                <Button
                  variant="primary-text"
                  size="sm"
                  beforeIcon="edit"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMemberMenuAnchor(e.currentTarget);
                  }}
                >
                  {t('ZustaendigeStelle.editButton')}
                </Button>
              }
            >
              <Stack spacing="m" orientation="vertical">
                <Alert
                  variant="info"
                  message={t('ZustaendigeStelle.memberManagement.infoMessage')}
                />
                <Text size="s" strong>
                  {t('ZustaendigeStelle.memberManagement.title')}
                </Text>
                <DescriptionList hasDivider>
                  {members.map((member: Member) => {
                    const roleKey =
                      member.role === 'owner'
                        ? 'ZustaendigeStelle.memberManagement.role.owner'
                        : member.role === 'moderator'
                          ? 'ZustaendigeStelle.memberManagement.role.moderator'
                          : 'ZustaendigeStelle.memberManagement.role.member';
                    const iconName =
                      member.role === 'owner'
                        ? 'star-filled'
                        : member.role === 'moderator'
                          ? 'user-role'
                          : 'user';
                    return (
                      <DescriptionList.Item
                        key={member.id}
                        title={member.name}
                        description={
                          <Stack
                            direction="horizontal"
                            spacing="xs"
                            alignItems="center"
                          >
                            <span>{t(roleKey)}</span>
                            <Icon name={iconName} size="sm" />
                          </Stack>
                        }
                      />
                    );
                  })}
                </DescriptionList>
              </Stack>
            </Accordion>
          </Accordion.Group>
          <Menu
            anchorElement={memberMenuAnchor}
            open={Boolean(memberMenuAnchor)}
            onClose={() => setMemberMenuAnchor(null)}
          >
            <Menu.MenuItem
              onClick={() => {
                setMemberMenuAnchor(null);
                setIsAddMemberModalVisible(true);
              }}
            >
              {t('ZustaendigeStelle.memberManagement.addMember')}
            </Menu.MenuItem>
            <Menu.MenuItem
              onClick={() => {
                setMemberMenuAnchor(null);
                setIsEditMembersModalVisible(true);
              }}
            >
              {t('ZustaendigeStelle.memberManagement.editMembers')}
            </Menu.MenuItem>
            <Menu.MenuItem
              onClick={() => {
                setMemberMenuAnchor(null);
                setIsChangeOwnerModalVisible(true);
              }}
            >
              {t('ZustaendigeStelle.memberManagement.changeOwner')}
            </Menu.MenuItem>
          </Menu>
        </div>
      )}

      {selectedPartner && isContactInfoModalVisible && (
        <EditKontaktinformationenModal
          onClose={() => {
            setIsContactInfoModalVisible(false);
          }}
          onSuccess={() => {
            setIsContactInfoModalVisible(false);
          }}
          isOpen
          zustaendigeStelle={selectedPartner}
        />
      )}
      {selectedPartner && isSystemPersonalizationModalVisible && (
        <EditSystempersonalisierungModal
          onClose={() => {
            setIsSystemPersonalizationModalVisible(false);
          }}
          onSuccess={() => {
            setIsSystemPersonalizationModalVisible(false);
          }}
          isOpen
          zustaendigeStelle={selectedPartner}
        />
      )}

      {selectedPartner && isAccessKeysModalVisible && (
        <EditMarketplaceAccessKeysModal
          onClose={() => {
            setIsAccessKeysModalVisible(false);
          }}
          onSuccess={() => {
            setIsAccessKeysModalVisible(false);
          }}
          isOpen
          zustaendigeStelle={selectedPartner}
        />
      )}

      {isAddMemberModalVisible && selectedPartner && (
        <AddMemberModal
          onClose={() => setIsAddMemberModalVisible(false)}
          onSuccess={() => setIsAddMemberModalVisible(false)}
          isOpen
          startAppId={selectedPartner.appiid}
        />
      )}

      {isEditMembersModalVisible && selectedPartner && (
        <EditMembersModal
          onClose={() => setIsEditMembersModalVisible(false)}
          onSuccess={() => setIsEditMembersModalVisible(false)}
          isOpen
          startAppId={selectedPartner.appiid}
        />
      )}

      {isChangeOwnerModalVisible && selectedPartner && (
        <ChangeOwnerModal
          onClose={() => setIsChangeOwnerModalVisible(false)}
          onSuccess={() => setIsChangeOwnerModalVisible(false)}
          isOpen
          startAppId={selectedPartner.appiid}
        />
      )}

      {isAddZustaendigeStelleModalVisible && (
        <AddZustaendigeStelleModal
          onClose={() => setIsAddZustaendigeStelleModalVisible(false)}
          onSuccess={(newPartnerId) => {
            setSelectedPartnerId(String(newPartnerId));
            setIsAddZustaendigeStelleModalVisible(false);
          }}
          isOpen
        />
      )}

      {isRenameZustaendigeStelleModalVisible && selectedPartner && (
        <RenameZustaendigeStelleModal
          onClose={() => setIsRenameZustaendigeStelleModalVisible(false)}
          onSuccess={() => {
            setIsRenameZustaendigeStelleModalVisible(false);
          }}
          isOpen
          zustaendigeStelle={{
            appiid: selectedPartner.appiid,
            dispname: selectedPartner.dispname
          }}
        />
      )}

      {isDeleteZustaendigeStelleModalVisible && selectedPartner && (
        <DeleteZustaendigeStelleModal
          onClose={() => setIsDeleteZustaendigeStelleModalVisible(false)}
          onSuccess={() => {
            setSelectedPartnerId(null);
            setIsInitialized(false);
            setIsDeleteZustaendigeStelleModalVisible(false);
          }}
          isOpen
          zustaendigeStelle={{
            appiid: selectedPartner.appiid,
            dispname: selectedPartner.dispname
          }}
        />
      )}
      {redirectUrlToExportMode && (
        <VVExpertModeRedirectModal
          isOpen
          onClose={() => setRedirectUrlToExportMode(null)}
          shouldBackTextBeVisible={false}
          onConfirm={() => {
            setRedirectUrlToExportMode(null);
            window.open(redirectUrlToExportMode, '_blank');
          }}
        />
      )}
    </Stack>
  );
}
